import { Divider, Modal, Skeleton, Tag, Typography } from "antd";
import { useReleaseHistory } from "@/hooks/use_version_poll";
import type {
  ReleaseHistoryEntry,
  VersionChangelogEntry,
  VersionManifest,
} from "@/hooks/use_version_poll";
import { formatBackendDate } from "@/utils/format_backend_datetime";

const { Paragraph, Text } = Typography;

const GROUP_META: Record<
  VersionChangelogEntry["group"],
  { label: string; color: string }
> = {
  neu: { label: "Neu", color: "green" },
  verbessert: { label: "Verbessert", color: "blue" },
  behoben: { label: "Behoben", color: "orange" },
};

const GROUP_ORDER: VersionChangelogEntry["group"][] = [
  "neu",
  "verbessert",
  "behoben",
];

/** Curated highlights of a single release, grouped; muted note when there are none. */
function ReleaseEntries({ entries }: { entries: VersionChangelogEntry[] }) {
  if (entries.length === 0) {
    return (
      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
        Keine größeren Änderungen — hauptsächlich Fehlerbehebungen und kleinere
        Verbesserungen im Hintergrund.
      </Paragraph>
    );
  }
  return (
    <>
      {GROUP_ORDER.map((group) => {
        const groupEntries = entries.filter((e) => e.group === group);
        if (groupEntries.length === 0) return null;
        const meta = GROUP_META[group];
        return (
          <div key={group} style={{ marginBottom: 12 }}>
            <Tag color={meta.color}>{meta.label}</Tag>
            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
              {groupEntries.map((entry, i) => (
                <li key={i}>{entry.text}</li>
              ))}
            </ul>
          </div>
        );
      })}
    </>
  );
}

/** "Version 1.4.0 · 13.07.2026" — date pinned to UTC per the app-wide convention. */
function releaseHeading(release: ReleaseHistoryEntry): string {
  const hasSemver = !!release.version && release.version !== "dev";
  const label = hasSemver ? `Version ${release.version}` : "Version";
  const date = formatBackendDate(release.releasedAt, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  return date ? `${label} · ${date}` : label;
}

/**
 * "Was ist neu" changelog dialog — a scrollable, newest-first history of all
 * releases, each showing its curated highlights (or a plain "no major changes"
 * note for highlight-free releases). The full history comes from /releases.json,
 * fetched lazily only while the dialog is open.
 *
 * If the history file is missing or unreadable (e.g. before the first
 * history-carrying deploy), it falls back to the polled latest `manifest` so the
 * dialog always shows at least the current release.
 */
export default function WhatsNewDialog({
  open,
  onClose,
  manifest,
}: {
  open: boolean;
  onClose: () => void;
  manifest: VersionManifest | null;
}) {
  const { releases, isLoading } = useReleaseHistory(open);

  const history: ReleaseHistoryEntry[] =
    releases.length > 0
      ? releases
      : manifest
        ? [
            {
              version: manifest.version,
              commit: manifest.commit,
              releasedAt: manifest.releasedAt,
              entries: manifest.entries,
            },
          ]
        : [];

  const latest = history[0] ?? null;
  const hasSemver = !!latest?.version && latest.version !== "dev";
  const title = hasSemver
    ? `Was ist neu in Version ${latest?.version}`
    : "Was ist neu";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={onClose}
      footer={null}
      title={title}
    >
      {isLoading && releases.length === 0 ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : history.length === 0 ? (
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Es liegen noch keine Versionshinweise vor.
        </Paragraph>
      ) : (
        <div style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: 4 }}>
          {history.map((release, idx) => (
            <div key={release.commit || idx}>
              {idx > 0 && <Divider style={{ margin: "16px 0" }} />}
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                {releaseHeading(release)}
              </Text>
              <ReleaseEntries entries={release.entries} />
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
